import { forwardRef } from 'react';
import {
  getNinePointLayoutProperties,
  StaggerCss,
} from '@mentimeter/question-modules-shared';
import { TextMarkupRenderer } from '@mentimeter/tiptap/text-markup-renderer';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { isEmptyObject } from '@tiptap/core';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import type { Question } from '@mentimeter/http-clients';
import type {
  FontOffsetT,
  InteractiveCanvasComponents,
} from '@mentimeter/question-modules-types';
import { canvasBlockStylesExtend } from './styles';

interface FreeTextPresentationProps {
  question: Question;
  shouldAnimate: boolean;
  CanvasTitleArea?: InteractiveCanvasComponents['CanvasTitleArea'];
  CanvasBodyArea?: InteractiveCanvasComponents['Body'];
  fontOffset: (arg0: FontOffsetT) => number;
}

export function FreeTextPresentation({
  question,
  shouldAnimate,
  CanvasTitleArea,
  CanvasBodyArea,
  fontOffset,
}: FreeTextPresentationProps) {
  const {
    question_description,
    layout,
    question_description_styled: descriptionStyled,
  } = question;

  const cleanDescription = question_description ?? '';

  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(layout);

  const breakpoints = [
    { count: 300, offset: -2 },
    { count: 200, offset: -1 },
  ];

  const fullText = cleanDescription.length;
  const width = 30;
  const height = Math.round(fullText / width);
  const offset = fontOffset({
    height,
    width,
    breakpoints,
  });

  const ConditionalWrapper = CanvasTitleArea ?? NonInteractiveWrapper;

  return (
    <ConditionalWrapper
      width="100%"
      alignItems={alignItems}
      justifyContent={justifyContent}
      height="100%"
    >
      {({ isDisabled }) => (
        <Box
          py="space1"
          px="space2"
          width="100%"
          alignItems={alignItems}
          height="100%"
          overflow="hidden"
        >
          <FlexibleText
            mb={0}
            textAlign={textAlign}
            fontOffset={offset}
            fontSize={6}
            color="text"
            width="100%"
            extend={canvasBlockStylesExtend}
            flex="1 1 auto"
          >
            <StaggerCss index={1} enableAnimations={shouldAnimate}>
              {CanvasBodyArea ? (
                CanvasBodyArea({
                  isDisabled,
                  formattedValue: descriptionStyled,
                  placeholder: undefined,
                  plainValue: cleanDescription,
                  justifyContent,
                  entityType: 'static-content',
                })
              ) : descriptionStyled && !isEmptyObject(descriptionStyled) ? (
                <Box
                  height="100%"
                  alignItems={alignItems}
                  justifyContent={justifyContent}
                >
                  <TextMarkupRenderer
                    allowBlockStyles
                    content={descriptionStyled}
                    editorContentProps={{
                      style: { width: '100%' },
                    }}
                  />
                </Box>
              ) : (
                cleanDescription
              )}
            </StaggerCss>
          </FlexibleText>
        </Box>
      )}
    </ConditionalWrapper>
  );
}

type NonInteractiveWrapperProps = Omit<BoxT, 'children'> & {
  children: ({ isDisabled }: { isDisabled: boolean }) => React.ReactNode;
};
const NonInteractiveWrapper = forwardRef(
  ({ children, ...props }: NonInteractiveWrapperProps, forwardedRef) => (
    <Box p="space4" {...props} ref={forwardedRef}>
      {children({ isDisabled: false })}
    </Box>
  ),
);
